import { cn } from '@/lib/utils'
import { ToggleLeft, ToggleRight } from 'lucide-react'
import { Tooltip } from './tooltip'
import { UnifiedButton } from './unified-button'

interface ToggleProps {
  enabled: boolean
  onToggle: () => void
  size?: 'sm' | 'md' | 'lg'
  showLabel?: boolean
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
  type: 'secondaryMovers' | 'recentExercises' | 'cycleMode'
  context?: 'sets' | 'routine' | 'charts' // Different tooltip content for different contexts
}

export const Toggle = ({
  enabled,
  onToggle,
  size = 'sm',
  showLabel = true,
  tooltipPosition = 'bottom',
  className = '',
  type,
  context = 'sets'
}: ToggleProps) => {
  const sizeConfig = {
    sm: { button: 'h-8 px-2', icon: 'h-4 w-4', text: 'text-xs' },
    md: { button: 'h-10 px-3', icon: 'h-5 w-5', text: 'text-sm' },
    lg: { button: 'h-12 px-4', icon: 'h-6 w-6', text: 'text-base' }
  }

  const config = sizeConfig[size]

  const getToggleConfig = () => {
    if (type === 'secondaryMovers') {
      return {
        label: 'Secondary Movers',
        tooltipContent:
          context === 'routine' ? (
            <div className="w-64 p-2 whitespace-normal">
              <div className="mb-2 flex text-sm font-semibold">Secondary Movers</div>
              <p className="text-muted-foreground mb-2 text-xs">
                Count secondary muscle groups as half sets (0.5x) in your routine volume.
              </p>
              <div className="bg-muted/30 space-y-1 rounded-md p-2 text-xs">
                <div>
                  <strong>Example:</strong> Bench Press
                </div>
                <div>• Primary: Chest (1.0x sets)</div>
                <div>• Secondary: Triceps, Front Delts (0.5x sets)</div>
              </div>
              <p className="text-muted-foreground mt-2 text-xs">
                Helps track total muscle volume more accurately
              </p>
            </div>
          ) : context === 'charts' ? (
            <div className="w-64 p-2 whitespace-normal">
              <div className="mb-2 text-sm font-semibold">Secondary Movers</div>
              <p className="text-muted-foreground mb-2 text-xs">
                Include secondary muscle groups in volume distribution chart.
              </p>
              <div className="bg-muted/30 space-y-1 rounded-md p-2 text-xs">
                <div>
                  <strong>Example:</strong> Bench Press
                </div>
                <div>• Primary: Chest (1.0 sets)</div>
                <div>• Secondary: Triceps (0.5 sets)</div>
              </div>
              <p className="text-muted-foreground mt-2 text-xs">
                Shows weighted muscle group contributions in workout analysis
              </p>
            </div>
          ) : (
            <div className="w-64 p-2 whitespace-normal">
              <div className="mb-2 text-sm font-semibold">Secondary Movers</div>
              <p className="text-muted-foreground mb-2 text-xs">
                Count secondary muscle groups as half sets (0.5x).
              </p>
              <div className="bg-muted/30 space-y-1 rounded-md p-2 text-xs">
                <div>
                  <strong>Example:</strong> Lat Pulldown
                </div>
                <div>• Primary: Lats (1.0x sets)</div>
                <div>• Secondary: Biceps (0.5x sets)</div>
              </div>
              <p className="text-muted-foreground mt-2 text-xs">
                Toggle to include/exclude secondary movers in set counts
              </p>
            </div>
          )
      }
    } else if (type === 'recentExercises') {
      return {
        label: 'Hide Inactive Exercises',
        tooltipContent: (
          <div className="w-64 p-2 whitespace-normal">
            <div className="mb-2 text-sm font-semibold">Exercise History Filter</div>
            <p className="text-muted-foreground mb-2 text-xs">
              Controls which exercises appear in your muscle group charts.
            </p>
            <div className="bg-muted/30 space-y-1 rounded-md p-2 text-xs">
              <div>
                <strong>Enabled:</strong> Only exercises performed in the last 14 days
              </div>
              <div>
                <strong>Disabled:</strong> All exercises from your training history
              </div>
            </div>
            <p className="text-muted-foreground mt-2 text-xs">
              Charts always show your complete historical data for each exercise. This only filters
              which exercises appear.
            </p>
          </div>
        )
      }
    } else if (type === 'cycleMode') {
      return {
        label: 'Continuous cycle',
        tooltipContent: (
          <div className="w-64 p-2 whitespace-normal">
            <div className="mb-2 text-sm font-semibold">Continuous Cycle Mode</div>
            <p className="text-muted-foreground mb-2 text-xs">
              Switch between weekly calendar-based training and continuous training cycles.
            </p>
            <div className="bg-muted/30 space-y-1 rounded-md p-2 text-xs">
              <div>
                <strong>Weekly (Off):</strong> Traditional Mon-Sun split
              </div>
              <div>
                <strong>Cycle (On):</strong> Continuous rotation (e.g., 5-day cycle)
              </div>
            </div>
            <p className="text-muted-foreground mt-2 text-xs">
              Perfect for programs that don&apos;t fit into a 7-day week
            </p>
          </div>
        )
      }
    }

    return { label: '', tooltipContent: null }
  }

  const { label, tooltipContent } = getToggleConfig()

  return (
    <Tooltip content={tooltipContent} position={tooltipPosition} className="w-auto">
      <UnifiedButton variant="ghost" size="sm" onClick={onToggle} className={cn(className)}>
        <div className="flex items-center gap-1">
          {enabled ? (
            <ToggleRight className={`${config.icon} text-green-500`} />
          ) : (
            <ToggleLeft className={`${config.icon} text-muted-foreground`} />
          )}
          {showLabel && (
            <span
              className={cn('hidden md:block', config.text, 'text-muted-foreground', {
                'text-foreground': enabled
              })}
            >
              {label}
            </span>
          )}
        </div>
      </UnifiedButton>
    </Tooltip>
  )
}

// Legacy component for backward compatibility
export const SecondaryMoversToggle = (
  props: Omit<ToggleProps, 'type'> & { context?: 'sets' | 'routine' | 'charts' },
  classname?: string
) => {
  return <Toggle {...props} type="secondaryMovers" className={classname} />
}
